import React from 'react';
import { Lock, LogOut, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * SubUserLockout — full-page interstitial shown when a SUB-USER (Team
 * Access member, e.g. a CA invited by the workspace owner) signs in to
 * a workspace whose subscription has expired.
 *
 * Why an interstitial (vs. the read-only nag the admin sees)?
 * ----------------------------------------------------------
 * The workspace owner has a financial decision to make and earns the
 * read-only nagware view. A sub-user has no purchasing authority — the
 * spec is explicit: "he should not be allowed to see any data or
 * pages as a sub-user of that user irrespective of the rights given
 * to the sub-user of this expired subscription user." We therefore
 * render this lockout in place of every route's children and prevent
 * any underlying page from mounting at all.
 *
 * The component does NOT call the renew endpoint — sub-users can't
 * renew on behalf of admins. The "Notify the workspace owner" button
 * opens the user's default mail client with a prefilled draft.
 */
export const SubUserLockout = () => {
  const { user, logout } = useAuth();

  const ownerEmail = user?.parent_admin_email || '';
  const subUserName = user?.sub_user_name || user?.name || '';

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleNotify = () => {
    // Prefilled mailto so the sub-user can ping their admin in one click.
    // Falls back gracefully if parent_admin_email isn't on the payload —
    // the user can still type it in.
    const subject = encodeURIComponent('[EquiTrack] Workspace subscription has expired');
    const body = encodeURIComponent(
      `Hi,\n\n`
      + `I tried logging in to your EquiTrack workspace but the subscription has expired, so I'm locked out.\n\n`
      + `Could you please renew the subscription at https://equitrack.in/pricing so we can resume work?\n\n`
      + `Thanks,\n${subUserName}`,
    );
    const url = ownerEmail
      ? `mailto:${ownerEmail}?subject=${subject}&body=${body}`
      : `mailto:?subject=${subject}&body=${body}`;
    window.location.href = url;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: '#FAF7EF' }}
      data-testid="sub-user-lockout-screen"
    >
      <Card className="max-w-md w-full border-0" style={{ backgroundColor: '#FFFFFF' }}>
        <CardContent className="p-8 sm:p-10">
          <div
            className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-5"
            style={{ backgroundColor: '#FBF0EC', border: '1px solid #E7CEC0' }}
          >
            <Lock className="h-6 w-6" style={{ color: '#9A3412', strokeWidth: 1.8 }} />
          </div>

          <h1
            className="text-2xl font-semibold text-center mb-3"
            style={{ color: '#1C1917', fontFamily: 'Work Sans' }}
            data-testid="sub-user-lockout-title"
          >
            Workspace access paused
          </h1>

          <p
            className="text-sm text-center mb-6 leading-relaxed"
            style={{ color: '#57534E' }}
            data-testid="sub-user-lockout-message"
          >
            The workspace owner&apos;s EquiTrack subscription has expired,
            so all sub-user access to portfolios, reports, and
            transactions is currently locked.
            <br /><br />
            Please ask <strong style={{ color: '#1B4332' }}>{ownerEmail || 'the workspace owner'}</strong> to
            renew the plan. Your permissions will resume automatically the
            moment renewal is completed — no re-invite needed.
          </p>

          <div
            className="rounded-lg p-3 text-xs mb-5"
            style={{ backgroundColor: '#FBF0EC', border: '1px solid #E7CEC0', color: '#7A2E18' }}
          >
            <p>
              <strong>Heads up:</strong> the data is safe and untouched.
              Nothing has been deleted — access just resumes the second
              the workspace owner renews.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              onClick={handleNotify}
              className="w-full text-white"
              style={{ backgroundColor: '#1B4332' }}
              data-testid="sub-user-lockout-notify-owner"
            >
              <Mail className="h-4 w-4 mr-2" style={{ strokeWidth: 1.8 }} />
              Notify the workspace owner
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleLogout}
              className="w-full"
              data-testid="sub-user-lockout-logout"
            >
              <LogOut className="h-4 w-4 mr-2" style={{ strokeWidth: 1.8 }} />
              Sign out
            </Button>
          </div>

          <p
            className="text-[11px] text-center mt-6"
            style={{ color: '#A8A29E' }}
          >
            Signed in as {subUserName || user?.email}
            {user?.parent_admin_email ? ` · Sub-user of ${user.parent_admin_email}` : ''}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubUserLockout;
